import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Action, Store } from '@ngrx/store';
import { TslDialog } from '@tesla/ng-tds';
import { StatusEnum } from 'app/profile/models/state.model';
import { SupplierAddressPropertyValues, SupplierContactPropertyValue } from 'app/profile/models/supplier.model';
import { CommonService } from 'app/profile/services/common.service';
import { ContactsService } from 'app/profile/services/contacts.service';
import { ProfileService } from 'app/profile/services/profile.service';
import { StateService } from 'app/profile/services/state.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { SelectSupplierContactList } from 'app/profile/store/app.selector';
import { CustomValidator } from 'app/shared/custom-validator/custom-validator';
import { I18nService } from 'app/shared/services/i18n.service';
import { updateValueAndValidity, validateAllFormFields } from 'app/shared/utils/utils';
import {
  LinkDelinkSupplierAddressContacts, LinkDelinkSupplierAddressContactsSuccess, LINK_DELINK_SUPPLIER_ADDRESS_CONTACTS_SUCCESS
} from 'app/profile/store/actions/supplierstate.action';
import { Subscription } from 'rxjs-compat';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';


@Component({
  selector: 'app-address-link-dialog',
  templateUrl: './address-link-dialog.component.html',
  styleUrls: ['./address-link-dialog.component.scss']
})
export class AddressLinkDialogComponent implements OnInit {
  contactForm: FormGroup;
  subscription: Subscription = {} as Subscription;
  contactList: Contact[];
  supplierCode: string;
  locationId: number;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddressLinkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private store: Store<SupplierProfileState>,
    private supplierStateService: SupplierStateService,
    private commonService: CommonService,
    private translateService: I18nService,
    private route: ActivatedRoute,
    private stateService: StateService,
    private dialog: TslDialog,
    private profileService: ProfileService,
    private actions$: Actions
  ) {
    this.subscription = new Subscription();
    this.contactForm = this.fb.group({
      contactType: [null, Validators.required],
      fname: [''],
      lname: [''],
      mobile: [''],
      email: [''],
      contacts: ['']
    });
  }

  get ContactFormControls() {
    return {
      contactType: this.contactForm.get('contactType'),
      fname: this.contactForm.get('fname'),
      lname: this.contactForm.get('lname'),
      mobile: this.contactForm.get('mobile'),
      email: this.contactForm.get('email'),
      contacts: this.contactForm.get('contacts')
    };
  }

  ngOnInit(): void {
    this.ContactFormControls.contactType.valueChanges.subscribe(value => {
      if (value == 'old') {
        this.ContactFormControls.contacts.setValidators(Validators.required);
        this.ContactFormControls.fname.clearValidators();
        this.ContactFormControls.lname.clearValidators();
        this.ContactFormControls.email.clearValidators();
        this.ContactFormControls.mobile.clearValidators();
      }
      else {
        this.ContactFormControls.contacts.clearValidators();
        this.ContactFormControls.fname.setValidators([Validators.required, CustomValidator.noWhitespaceValidator, Validators.pattern("^([a-zA-Z\\s]+)$")]);
        this.ContactFormControls.lname.setValidators([Validators.required, CustomValidator.noWhitespaceValidator, Validators.pattern("^([a-zA-Z\\s]+)$")]);
        this.ContactFormControls.email.setValidators([Validators.required, Validators.email]);
        this.ContactFormControls.mobile.setValidators([Validators.required, CustomValidator.noWhitespaceValidator]);
      }
      this.ContactFormControls.contacts.updateValueAndValidity();
      this.ContactFormControls.fname.updateValueAndValidity();
      this.ContactFormControls.lname.updateValueAndValidity();
      this.ContactFormControls.email.updateValueAndValidity();
      this.ContactFormControls.mobile.updateValueAndValidity();
    });

    const warehouseContacts: SupplierAddressPropertyValues = this.dialogData?.warehouseAddress;
    this.locationId = warehouseContacts.locationId.newValue;
    this.supplierCode = this.dialogData?.supplierCode;
    const formattedContacts: Contact[] = [];
    this.store.select(SelectSupplierContactList).subscribe((contacts: SupplierContactPropertyValue[]) => {
      if (contacts) {
        contacts.forEach(contact => {
          const c = new Contact();
          c.firstName = contact.firstName.newValue;
          c.lastName = contact.lastName.newValue;
          c.email = contact.email.newValue;
          c.phone = contact.phone.newValue;
          const index = warehouseContacts.contacts.newValue.findIndex(contact => contact['email'] === c.email);
          if (index < 0) formattedContacts.push(c);
        });
      }
    });
    this.contactList = formattedContacts;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  save() {
    if (this.contactForm.valid) {
      let addressContacts = {
        contacts: this.dialogData?.warehouseAddress.contacts.newValue ? this.dialogData?.warehouseAddress.contacts.newValue : [],
        locationId: this.locationId
      };
      // Filter contact with all property
      let allFieldsContacts = addressContacts.contacts.filter(x => x.name && x.email && x.phone);

      // Filter contact with any property missing
      let allContactsWithMissingData = addressContacts.contacts.filter(x => !(x.name && x.email && x.phone));
      addressContacts.contacts = [];
      addressContacts.contacts.push(...allFieldsContacts);

      if (this.ContactFormControls.contactType.value === 'new') {
        this.dialog
          .alert(
            'Alert',
            this.translateService.get('Contact added here will not be added to your Supplier Tesla profile. If this contact needs Tesla Portal account, please have your Primary Contact add this individual via the Contact Tab in Profile.')
          )
          .afterClosed()
          .subscribe(r => {
            addressContacts.contacts.push({
              name: this.ContactFormControls.fname.value + ' ' + this.ContactFormControls.lname.value,
              email: this.ContactFormControls.email.value,
              phone: this.ContactFormControls.mobile.value
            });

            addressContacts.contacts.push(...allContactsWithMissingData);

            this.profileService.updateLocationContacts(this.supplierCode, addressContacts).subscribe((res: any) => {
              if (res) {
                this.stateService.currentStatus.next({ type: StatusEnum.Success, message: 'Contact linked' });
                this.dialogRef.close(true);
              }
            });
          }, err => {
            this.stateService.currentStatus.next({ type: StatusEnum.Error, message: this.translateService.get('Error Occurred while linking contact or you don\'t have permission to link contact.') });
            this.dialogRef.close(false);
          });

      } else {
        const contacts: any[] = this.ContactFormControls.contacts.value;

        contacts.forEach(x => {
          addressContacts.contacts.push({
            name: x.firstName + ' ' + x.lastName,
            email: x.email,
            phone: x.phone
          });
        });
        addressContacts.contacts.push(...allContactsWithMissingData);
        this.store.dispatch(new LinkDelinkSupplierAddressContacts({supplierCode: this.supplierCode, contacts: addressContacts}));
        this.actions$.pipe(
          ofType<LinkDelinkSupplierAddressContactsSuccess>(LINK_DELINK_SUPPLIER_ADDRESS_CONTACTS_SUCCESS),
          takeUntil(this.unsubscribe$)
        )
        .subscribe(() => {
          this.stateService.currentStatus.next({ type: StatusEnum.Success, message: 'Contact(s) linked' });
          this.dialogRef.close(true);
      });

     }
    }
    else {
      validateAllFormFields(this.contactForm);
    }

  }

  ngOnDestroy(){
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

export class Contact {
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  phone: string = '';
  getFullName(): string {
    return this.firstName + this.lastName;
  }

  constructor() { }
}
